import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import {
  SITE_URL,
  OG_IMAGE,
  PATH_TO_KEY,
  NOINDEX_KEYS,
  getSeoMeta,
} from "../i18n/seo_routes";

const HREF_MAP = { es: "es-ES", en: "en-US", sk: "sk-SK", de: "de-DE", "zh-CN": "zh-CN" };

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const m = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href, attrs = {}) => {
  let sel = `link[rel="${rel}"]`;
  Object.entries(attrs).forEach(([k, v]) => (sel += `[${k}="${v}"]`));
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const buildHomeGraph = (lang, seo) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#org`,
      name: "RE₿EL BTC",
      alternateName: ["Academia RE₿EL", "REBEL", "REBEL BTC"],
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: OG_IMAGE, width: 1200, height: 630 },
      description: seo.description,
      email: "rebelbtc@rebelbtc.com",
      foundingDate: "2024",
      sameAs: [],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#site`,
      url: SITE_URL,
      name: "RE₿EL BTC",
      publisher: { "@id": `${SITE_URL}/#org` },
      inLanguage: ["es-ES", "en-US", "sk-SK"],
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "Course",
      "@id": `${SITE_URL}/academy#course`,
      name:
        lang === "es"
          ? "Curso Profesional de Trading de Criptomonedas — Academia RE₿EL"
          : lang === "en"
          ? "Professional Crypto Trading Course — RE₿EL Academy"
          : "Profesionálny krypto trading kurz — RE₿EL Akadémia",
      description:
        lang === "es"
          ? "51 lecciones en 6 niveles: fundamentos, blockchain, análisis técnico, Smart Money Concepts, gestión de riesgo y trading cuantitativo. Incluye Mentor IA Claude Sonnet 4.5 y Market Simulator™."
          : lang === "en"
          ? "51 lessons across 6 levels: fundamentals, blockchain, technical analysis, Smart Money Concepts, risk management and quant trading. Includes Claude Sonnet 4.5 AI Mentor and Market Simulator™."
          : "51 lekcií v 6 úrovniach: základy, blockchain, technická analýza, Smart Money Concepts, riadenie rizika a kvantitatívny trading. Vrátane Claude Sonnet 4.5 AI Mentora a Market Simulator™.",
      provider: { "@id": `${SITE_URL}/#org` },
      url: `${SITE_URL}/academy`,
      inLanguage: ["es", "en", "sk"],
      educationalLevel: "Beginner to Expert",
      coursePrerequisites: lang === "es" ? "Ninguno" : lang === "en" ? "None" : "Žiadne",
      numberOfCredits: 6,
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "online",
        courseWorkload: "PT51H",
        instructor: { "@type": "Organization", name: "Academia RE₿EL · AI Mentor" },
      },
      offers: [
        {
          "@type": "Offer",
          name: lang === "es" ? "Plan Gratuito" : lang === "en" ? "Free Plan" : "Bezplatný plán",
          price: "0",
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
          url: `${SITE_URL}/academy`,
        },
        {
          "@type": "Offer",
          name: lang === "es" ? "Plan PRO Lifetime" : lang === "en" ? "PRO Lifetime Plan" : "PRO doživotný plán",
          price: "39.90",
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
          url: SITE_URL,
          description:
            lang === "es"
              ? "Acceso de por vida a todo el contenido + Mentor IA ilimitado + Market Simulator™ completo"
              : lang === "en"
              ? "Lifetime access to all content + unlimited AI Mentor + full Market Simulator™"
              : "Doživotný prístup k všetkému obsahu + neobmedzený AI Mentor + plný Market Simulator™",
        },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/simulator#app`,
      name: "RE₿EL Market Simulator™",
      applicationCategory: "EducationalApplication",
      operatingSystem: "Web",
      url: `${SITE_URL}/simulator`,
      description:
        lang === "es"
          ? "Simulador de trading cripto en tiempo real. Revive eventos históricos (FTX, ETF, COVID) con IA Mentor en vivo y análisis Trader DNA™."
          : lang === "en"
          ? "Realtime crypto trading simulator. Relive historic events (FTX, ETF, COVID) with live AI Mentor and Trader DNA™ analysis."
          : "Krypto trading simulátor v reálnom čase. Prežij historické udalosti (FTX, ETF, COVID) so živým AI Mentorom a analýzou Trader DNA™.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      publisher: { "@id": `${SITE_URL}/#org` },
    },
    {
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/liquidity-map#app`,
      name: "RE₿EL Liquidity Map",
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      url: `${SITE_URL}/liquidity-map`,
      description:
        lang === "es"
          ? "Mapa de liquidaciones crypto con 27 exchanges CEX+DEX, datos DeFi on-chain (Aave v3, Morpho), detector de ballenas >$1M y Predictor IA de cascadas en tiempo real."
          : lang === "en"
          ? "Crypto liquidation map with 27 CEX+DEX exchanges, on-chain DeFi data (Aave v3, Morpho), whale detector >$1M and real-time AI cascade predictor."
          : "Mapa krypto likvidácií s 27 CEX+DEX burzami, on-chain DeFi dáta (Aave v3, Morpho), detektor veľrýb >$1M a AI prediktor kaskád v reálnom čase.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        description: lang === "es"
          ? "30 días de prueba gratuita"
          : lang === "en"
          ? "30-day free trial"
          : "30-dňová bezplatná skúška",
      },
      featureList:
        lang === "es"
          ? [
              "27 exchanges en tiempo real",
              "Datos DeFi on-chain Aave y Morpho",
              "Detector de ballenas >$1M",
              "Predictor IA de cascadas",
              "Simulador de liquidación personal",
              "Filtro CEX vs DEX único en el mercado",
            ]
          : lang === "en"
          ? [
              "27 exchanges in real time",
              "On-chain DeFi data Aave and Morpho",
              "Whale detector >$1M",
              "AI cascade predictor",
              "Personal liquidation simulator",
              "Unique CEX vs DEX filter",
            ]
          : [
              "27 búrz v reálnom čase",
              "On-chain DeFi dáta Aave a Morpho",
              "Detektor veľrýb >$1M",
              "AI prediktor kaskád",
              "Osobný simulátor likvidácie",
              "Jedinečný CEX vs DEX filter",
            ],
      publisher: { "@id": `${SITE_URL}/#org` },
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿Qué es el Liquidity Map de RE₿EL?"
              : lang === "en"
              ? "What is the RE₿EL Liquidity Map?"
              : "Čo je RE₿EL Liquidity Map?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "Es el único mapa de liquidaciones crypto que agrega datos de 27 exchanges CEX y DEX en tiempo real, incluyendo datos DeFi on-chain de Aave y Morpho, detector de ballenas con órdenes >$1M, y un Predictor IA de cascadas de liquidación."
                : lang === "en"
                ? "It is the only crypto liquidation map that aggregates data from 27 CEX and DEX exchanges in real time, including on-chain DeFi data from Aave and Morpho, a whale detector for orders >$1M, and an AI Liquidation Cascade Predictor."
                : "Je to jediná mapa krypto likvidácií, ktorá agreguje dáta z 27 CEX a DEX búrz v reálnom čase, vrátane on-chain DeFi dát z Aave a Morpho, detektora veľrýb pre objednávky >$1M a AI prediktora kaskád likvidácií.",
          },
        },
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿Es gratis el Liquidity Map?"
              : lang === "en"
              ? "Is the Liquidity Map free?"
              : "Je Liquidity Map zadarmo?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "Sí, todos los usuarios registrados tienen 30 días de prueba gratuita completa. Después requiere acceso PRO."
                : lang === "en"
                ? "Yes, all registered users get a full 30-day free trial. After that PRO access is required."
                : "Áno, všetci registrovaní používatelia majú 30-dňovú plnohodnotnú bezplatnú skúšku. Potom je potrebný PRO prístup.",
          },
        },
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿En qué se diferencia del Liquidity Map de CoinGlass?"
              : lang === "en"
              ? "How does it differ from the CoinGlass Liquidity Map?"
              : "Ako sa líši od CoinGlass Liquidity Map?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "RE₿EL agrega 27 exchanges vs los CEX de CoinGlass, incluye datos DeFi on-chain exactos (no estimaciones), detector de ballenas en tiempo real y Predictor IA de cascadas. Todo gratis con trial."
                : lang === "en"
                ? "RE₿EL aggregates 27 exchanges vs CoinGlass's CEX-only set, includes exact on-chain DeFi data (not estimates), a real-time whale detector and an AI cascade predictor. All free during the trial."
                : "RE₿EL agreguje 27 búrz oproti len CEX súbory CoinGlass, obsahuje presné on-chain DeFi dáta (nie odhady), detektor veľrýb v reálnom čase a AI prediktor kaskád. Všetko zadarmo počas skúšky.",
          },
        },
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿Necesito experiencia previa para empezar?"
              : lang === "en"
              ? "Do I need prior experience to start?"
              : "Potrebujem skúsenosti, aby som mohol začať?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "No. El Nivel 1 comienza desde cero explicando qué es Bitcoin, blockchain y cómo funciona el mercado cripto."
                : lang === "en"
                ? "No. Level 1 starts from zero explaining Bitcoin, blockchain and how the crypto market works."
                : "Nie. Úroveň 1 začína od nuly — Bitcoin, blockchain a ako funguje krypto trh.",
          },
        },
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿El pago es mensual o de por vida?"
              : lang === "en"
              ? "Is the payment monthly or lifetime?"
              : "Je platba mesačná alebo doživotná?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "El Plan PRO es un pago único de 39.90 €. Sin suscripción. Acceso de por vida incluyendo actualizaciones futuras."
                : lang === "en"
                ? "The PRO plan is a one-time payment of €39.90. No subscription. Lifetime access including future updates."
                : "PRO plán je jednorazová platba 39,90 €. Bez predplatného. Doživotný prístup vrátane budúcich aktualizácií.",
          },
        },
        {
          "@type": "Question",
          name: lang === "es" ? "¿Qué incluye el Plan PRO?" : lang === "en" ? "What does the PRO plan include?" : "Čo zahŕňa PRO plán?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "Las 51 lecciones completas en 6 niveles, Mentor IA Claude Sonnet 4.5 ilimitado, Market Simulator™ completo (4 modos), lectura por voz Onyx, historial y Trader DNA™ con análisis psicológico personalizado."
                : lang === "en"
                ? "All 51 lessons across 6 levels, unlimited Claude Sonnet 4.5 AI Mentor, full Market Simulator™ (4 modes), Onyx voice reading, history and Trader DNA™ with personalised psychological analysis."
                : "Všetkých 51 lekcií v 6 úrovniach, neobmedzený Claude Sonnet 4.5 AI Mentor, plný Market Simulator™ (4 režimy), Onyx hlasové čítanie, história a Trader DNA™ s personalizovanou analýzou.",
          },
        },
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿En qué idiomas está disponible?"
              : lang === "en"
              ? "What languages is it available in?"
              : "V akých jazykoch je dostupný?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "La plataforma está disponible en Español, Inglés y Eslovaco — incluyendo lecciones, Mentor IA, Market Simulator™ y voz Onyx."
                : lang === "en"
                ? "The platform is available in Spanish, English and Slovak — including lessons, AI Mentor, Market Simulator™ and Onyx voice."
                : "Platforma je dostupná v španielčine, angličtine a slovenčine — vrátane lekcií, AI Mentora, Market Simulator™ a Onyx hlasu.",
          },
        },
        {
          "@type": "Question",
          name:
            lang === "es"
              ? "¿Es asesoramiento financiero?"
              : lang === "en"
              ? "Is this financial advice?"
              : "Je to finančné poradenstvo?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              lang === "es"
                ? "No. RE₿EL BTC ofrece exclusivamente contenido educativo. No es asesoramiento financiero ni recomendación de inversión."
                : lang === "en"
                ? "No. RE₿EL BTC provides educational content only. Not financial advice nor an investment recommendation."
                : "Nie. RE₿EL BTC poskytuje výhradne vzdelávací obsah. Nie je to finančné poradenstvo ani investičné odporúčanie.",
          },
        },
      ],
    },
  ],
});

const buildPageGraph = (pageKey, seo, lang) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE_URL}${seo.path}#page`,
  url: `${SITE_URL}${seo.path}`,
  name: seo.title,
  description: seo.description,
  inLanguage: HREF_MAP[lang] || "es-ES",
  isPartOf: { "@id": `${SITE_URL}/#site` },
  breadcrumb: {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "RE₿EL BTC", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: seo.title, item: `${SITE_URL}${seo.path}` },
    ],
  },
});

/**
 * <SEO /> — call once per route. Without props, auto-detects the page from window.location.pathname.
 * Pass `pageKey` to override. Updates: title, description, keywords, robots, canonical,
 * hreflang alternates, Open Graph, Twitter cards, and JSON-LD (Organization+WebSite+Course+FAQ on home,
 * WebPage+BreadcrumbList on inner pages).
 */
export const SEO = ({ pageKey: pageKeyProp } = {}) => {
  const { lang } = useLanguage();
  const location = useLocation();
  const detected = PATH_TO_KEY[location.pathname] || "home";
  const pageKey = pageKeyProp || detected;
  const seo = getSeoMeta(pageKey, lang);
  const hreflang = HREF_MAP[lang] || "es-ES";
  const noindex = NOINDEX_KEYS.has(pageKey);

  // Dynamic article pages own their meta + JSON-LD entirely. Skip global override.
  const isArticleRoute =
    /^\/blog\/[^/]+$/.test(location.pathname) ||
    /^\/topwallet\/[^/]+\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    if (isArticleRoute) return;
    // ---- Title + html lang ----
    document.title = seo.title;
    document.documentElement.lang = lang;

    // ---- Basic meta ----
    setMeta('meta[name="description"]', "content", seo.description);
    if (seo.keywords) setMeta('meta[name="keywords"]', "content", seo.keywords);
    setMeta('meta[name="author"]', "content", "Academia RE₿EL — rebelbtc.com");
    setMeta(
      'meta[name="robots"]',
      "content",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    );
    setMeta('meta[name="googlebot"]', "content", noindex ? "noindex, nofollow" : "index, follow");
    setMeta('meta[name="theme-color"]', "content", "#07080f");
    setMeta('meta[name="application-name"]', "content", "RE₿EL BTC");
    setMeta('meta[name="apple-mobile-web-app-title"]', "content", "RE₿EL");
    setMeta('meta[name="format-detection"]', "content", "telephone=no");

    // ---- Canonical + hreflang ----
    setLink("canonical", `${SITE_URL}${seo.path}`);
    setLink("alternate", `${SITE_URL}${seo.path}?lang=es`, { hreflang: "es-ES" });
    setLink("alternate", `${SITE_URL}${seo.path}?lang=en`, { hreflang: "en-US" });
    setLink("alternate", `${SITE_URL}${seo.path}?lang=sk`, { hreflang: "sk-SK" });
    setLink("alternate", `${SITE_URL}${seo.path}`, { hreflang: "x-default" });

    // ---- Open Graph ----
    setMeta('meta[property="og:type"]', "content", pageKey === "home" ? "website" : "article");
    setMeta('meta[property="og:site_name"]', "content", "RE₿EL BTC");
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:url"]', "content", `${SITE_URL}${seo.path}`);
    setMeta('meta[property="og:image"]', "content", OG_IMAGE);
    setMeta('meta[property="og:image:width"]', "content", "1200");
    setMeta('meta[property="og:image:height"]', "content", "1200");
    setMeta('meta[property="og:image:alt"]', "content", "RE₿EL BTC · Academia RE₿EL");
    setMeta('meta[property="og:locale"]', "content", hreflang.replace("-", "_"));

    // ---- Twitter Card ----
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
    setMeta('meta[name="twitter:image"]', "content", OG_IMAGE);
    setMeta('meta[name="twitter:image:alt"]', "content", "RE₿EL BTC · Academia RE₿EL");

    // ---- JSON-LD ----
    const data =
      pageKey === "home" ? buildHomeGraph(lang, seo) : buildPageGraph(pageKey, seo, lang);
    setJsonLd("rebel-jsonld", data);
  }, [lang, pageKey, seo.title, seo.description, seo.path, hreflang, noindex, isArticleRoute]);

  return null;
};

export default SEO;
